import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Warning,
  TrendingDown,
  FilterList,
  Inventory,
} from '@mui/icons-material';
import DataTable, { Column } from '../common/DataTable';
import ProductForm from './ProductForm';
import StockAdjustmentDialog from './StockAdjustmentDialog';
import { Product, productService, CreateProductData, UpdateProductData, ProductFilters } from '../../services/productService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

const ProductList: React.FC = () => {
  const dispatch = useAppDispatch();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProductFilters>({});
  
  // Form states
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Stock adjustment states
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockProduct, setStockProduct] = useState<Product | null>(null);
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuProduct, setMenuProduct] = useState<Product | null>(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, searchTerm, filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await productService.getProducts({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        ...filters,
      });
      setProducts(response.products);
      setTotalCount(response.pagination.total);
    } catch (error) {
      console.error('Error fetching products:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch products',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setFormOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setFormOpen(true);
    setAnchorEl(null);
  };

  const handleViewProduct = (product: Product) => {
    // TODO: Implement product details view
    console.log('View product:', product);
    setAnchorEl(null);
  };

  const handleStockAdjustment = (product: Product) => {
    setStockProduct(product);
    setStockDialogOpen(true);
    setAnchorEl(null);
  };

  const handleToggleStatus = async (product: Product) => {
    try {
      await productService.toggleProductStatus(product.id);
      dispatch(addNotification({
        type: 'success',
        message: `Product ${product.isActive ? 'deactivated' : 'activated'} successfully`,
      }));
      fetchProducts();
    } catch (error) {
      console.error('Error toggling product status:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to update product status',
      }));
    }
    setAnchorEl(null);
  };

  const handleDeleteProduct = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await productService.deleteProduct(productToDelete.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Product deleted successfully',
      }));
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to delete product',
      }));
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleFormSubmit = async (data: CreateProductData | UpdateProductData) => {
    setFormLoading(true);
    try {
      if (selectedProduct) {
        await productService.updateProduct(selectedProduct.id, data as UpdateProductData);
        dispatch(addNotification({
          type: 'success',
          message: 'Product updated successfully',
        }));
      } else {
        await productService.createProduct(data as CreateProductData);
        dispatch(addNotification({
          type: 'success',
          message: 'Product added successfully',
        }));
      }
      setFormOpen(false);
      fetchProducts();
    } catch (error: any) {
      console.error('Error saving product:', error);
      dispatch(addNotification({
        type: 'error',
        message: error.response?.data?.error || 'Failed to save product',
      }));
    } finally {
      setFormLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: Product) => {
    setAnchorEl(event.currentTarget);
    setMenuProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuProduct(null);
  };

  const columns: Column[] = [
    {
      id: 'name',
      label: 'Product',
      format: (_value: any, row: Product) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.brand} • {row.sku}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      format: (value: string) => (
        <Chip
          label={value}
          size="small"
          sx={{
            bgcolor: 'rgba(232, 180, 184, 0.1)',
            color: '#E8B4B8',
            fontWeight: 600,
          }}
        />
      ),
    },
    {
      id: 'currentStock',
      label: 'Stock Status',
      format: (_value: any, row: Product) => {
        const percentage = (row.currentStock / row.maxStockLevel) * 100;
        const isLow = row.currentStock <= row.minStockLevel;
        const isExpiring = row.isExpiringSoon;
        
        return (
          <Box sx={{ minWidth: 140 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" color={isLow ? 'error.main' : 'text.primary'}>
                {row.currentStock} {row.unit}
              </Typography>
              {isLow && <Warning color="error" fontSize="small" />}
              {isExpiring && <TrendingDown color="warning" fontSize="small" />}
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(percentage, 100)}
              color={isLow ? 'error' : percentage > 70 ? 'success' : 'warning'}
              sx={{ height: 4, borderRadius: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              Min: {row.minStockLevel} • Max: {row.maxStockLevel}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'costPrice',
      label: 'Cost Price',
      format: (value: number) => `₹${value.toFixed(2)}`,
    },
    {
      id: 'sellingPrice',
      label: 'Selling Price',
      format: (value: number) => `₹${value.toFixed(2)}`,
    },
    {
      id: 'profitMargin',
      label: 'Profit',
      format: (_value: any, row: Product) => {
        const margin = row.profitMargin || 0;
        return (
          <Typography 
            variant="body2" 
            color={margin > 0 ? 'success.main' : 'error.main'}
            fontWeight="bold"
          >
            {margin.toFixed(1)}%
          </Typography>
        );
      },
    },
    {
      id: 'expiryDate',
      label: 'Expiry',
      format: (_value: any, row: Product) => {
        if (!row.expiryDate) return 'N/A';
        
        const expiryDate = new Date(row.expiryDate);
        const daysUntilExpiry = row.daysUntilExpiry || 0;
        
        return (
          <Box>
            <Typography 
              variant="body2" 
              color={daysUntilExpiry < 30 ? 'error.main' : 'text.primary'}
            >
              {expiryDate.toLocaleDateString()}
            </Typography>
            {daysUntilExpiry < 30 && daysUntilExpiry > 0 && (
              <Typography variant="caption" color="error.main">
                {daysUntilExpiry} days left
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'isActive',
      label: 'Status',
      format: (value: boolean) => (
        <Chip
          label={value ? 'Active' : 'Inactive'}
          color={value ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      format: (_value: any, row: Product) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          sx={{
            color: '#E8B4B8',
            '&:hover': {
              bgcolor: 'rgba(232, 180, 184, 0.1)',
            },
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Product Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddProduct}
          sx={{
            bgcolor: '#E8B4B8',
            fontWeight: 700,
            px: 3,
            py: 1.5,
            borderRadius: 3,
            '&:hover': {
              bgcolor: '#D4A4A8',
            },
          }}
        >
          Add Product
        </Button>
      </Box>

      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          bgcolor: 'rgba(232, 180, 184, 0.05)',
          borderRadius: 3,
          border: '1px solid rgba(232, 180, 184, 0.1)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#E8B4B8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover fieldset': { borderColor: '#E8B4B8' },
                  '&.Mui-focused fieldset': { borderColor: '#E8B4B8' },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value || undefined }))}
                label="Category"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="Makeup">Makeup</MenuItem>
                <MenuItem value="Hair Care">Hair Care</MenuItem>
                <MenuItem value="Skincare">Skincare</MenuItem>
                <MenuItem value="Nail Care">Nail Care</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select
                value={filters.brand || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value || undefined }))}
                label="Brand"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Brands</MenuItem>
                <MenuItem value="MAC">MAC</MenuItem>
                <MenuItem value="Loreal">Loreal</MenuItem>
                <MenuItem value="Maybelline">Maybelline</MenuItem>
                <MenuItem value="Lakme">Lakme</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.isActive !== undefined ? filters.isActive.toString() : ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  isActive: e.target.value === '' ? undefined : e.target.value === 'true' 
                }))}
                label="Status"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Stock Level</InputLabel>
              <Select
                value={filters.lowStock ? 'low' : ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  lowStock: e.target.value === 'low' ? true : undefined 
                }))}
                label="Stock Level"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#E8B4B8' },
                }}
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setFilters({})}
              sx={{
                borderColor: '#E8B4B8',
                color: '#E8B4B8',
                fontWeight: 600,
                borderRadius: 2,
                height: 56,
                minWidth: 'auto',
                px: 2,
                '&:hover': {
                  borderColor: '#D4A4A8',
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                },
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Table */}
      <Card 
        sx={{ 
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(232, 180, 184, 0.2)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            rows={products}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            loading={loading}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(232, 180, 184, 0.2)',
          },
        }}
      >
        <MenuItem onClick={() => handleViewProduct(menuProduct!)}>
          <Visibility sx={{ mr: 2, color: '#06B6D4' }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleEditProduct(menuProduct!)}>
          <Edit sx={{ mr: 2, color: '#F59E0B' }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleStockAdjustment(menuProduct!)}>
          <Inventory sx={{ mr: 2, color: '#8B5CF6' }} />
          Adjust Stock
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(menuProduct!)}>
          {menuProduct?.isActive ? (
            <>
              <Warning sx={{ mr: 2, color: '#EF4444' }} />
              Deactivate
            </>
          ) : (
            <>
              <Inventory sx={{ mr: 2, color: '#10B981' }} />
              Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDeleteProduct(menuProduct!)}>
          <Delete sx={{ mr: 2, color: '#EF4444' }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Product Form Dialog */}
      <ProductForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        product={selectedProduct}
        loading={formLoading}
      />

      {/* Stock Adjustment Dialog */}
      <StockAdjustmentDialog
        open={stockDialogOpen}
        onClose={() => setStockDialogOpen(false)}
        product={stockProduct}
        onSuccess={fetchProducts}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#EF4444' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The product will be permanently removed from the system.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{productToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteProduct}
            variant="contained"
            color="error"
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductList;